import { Injectable, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UserProfile } from './models';
import { I18nService } from './i18n.service';
import { ApiService, PrivacyExportRequestResponse, SoftwareMfaChallenge } from './api.service';

@Injectable({ providedIn: 'root' })
export class SessionService {
  readonly currentUser = signal<UserProfile | null>(null);
  readonly adminStatus = signal<{ isAdmin: boolean; isModerator: boolean; email?: string; groups?: string[] } | null>(null);
  readonly loginErrorKey = signal('');
  readonly loginErrorMessage = signal('');
  readonly isRestoringSession = signal(false);
  readonly pendingMfaChallenge = signal<SoftwareMfaChallenge | null>(null);
  readonly sessionExpiredMessageKey = signal('');
  readonly pendingOAuthProvider = signal('');
  readonly loginError = computed(() => this.loginErrorKey() ? this.i18n.t(this.loginErrorKey()) : '');
  readonly isLoggedIn = computed(() => this.currentUser() !== null);
  readonly isAdmin = computed(() => this.adminStatus()?.isAdmin === true);
  readonly isModerator = computed(() => this.adminStatus()?.isModerator === true || this.adminStatus()?.isAdmin === true);
  readonly needsRequiredProfileCompletion = computed(() => this.hasMissingRequiredProfileFields(this.currentUser()));

  private restorePromise: Promise<boolean> | null = null;
  private sessionExpired = false;
  private readonly pendingPictureDeletionStorageKey = 'phdcompass.pendingProfilePictureDeletionUrl';
  private readonly oauthStartAfterLogoutStorageKey = 'phdcompass.oauth.startAfterLogout';
  private readonly oauthResumeProviderStorageKey = 'phdcompass.oauth.resumeProviderAfterLogout';
  private readonly oauthProviderAfterLogoutStorageKey = 'phdcompass.oauth.providerAfterLogout';
  private readonly oauthStateStorageKey = 'phdcompass.oauth.state';
  private readonly oauthVerifierStorageKey = 'phdcompass.oauth.codeVerifier';
  private readonly oauthRedirectStorageKey = 'phdcompass.oauth.redirectTo';
  private readonly oauthCallbackCodeStorageKey = 'phdcompass.oauth.callbackCode';
  private readonly oauthLinkModeStorageKey = 'phdcompass.oauth.linkMode';
  private readonly microsoftAvatarImportAttemptStorageKey = 'phdcompass.microsoftAvatarImport.attempted';
  private readonly oauthProviders = {
    google: { name: 'Google', unavailableKey: 'auth.googleUnavailable', displayName: 'Google', scopes: 'openid email profile' },
    facebook: { name: 'Facebook', unavailableKey: 'auth.facebookUnavailable', displayName: 'Facebook', scopes: 'openid email profile' },
    amazon: { name: 'LoginWithAmazon', unavailableKey: 'auth.amazonUnavailable', displayName: 'Amazon', scopes: 'openid email profile' },
    apple: { name: 'SignInWithApple', unavailableKey: 'auth.appleUnavailable', displayName: 'Apple', scopes: 'openid email profile' },
    microsoft: { name: 'Microsoft', unavailableKey: 'auth.microsoftUnavailable', displayName: 'Microsoft', scopes: 'openid email profile' },
    orcid: { name: 'ORCID', unavailableKey: 'auth.orcidUnavailable', displayName: 'ORCID', scopes: 'openid email' },
    linkedin: { name: 'LinkedIn', unavailableKey: 'auth.linkedinUnavailable', displayName: 'LinkedIn', scopes: 'openid email profile' },
    x: { name: 'XTwitter', unavailableKey: 'auth.xUnavailable', displayName: 'X', scopes: 'users.read tweet.read offline.access' }
  } as const;

  constructor(private i18n: I18nService, private api: ApiService, private router: Router) {
    window.addEventListener('phdcompass-auth-expired', () => this.expireSession());
    this.clearMicrosoftAvatarImportAttemptsAfterCallback();
    if (this.resumeOAuthAfterHostedUiLogout()) return;
    if (this.api.hasToken && !this.isOAuthCallbackRequest()) void this.ensureSession();
  }

  async login(username: string, password: string): Promise<boolean> {
    try {
      const result = await this.api.login(username.trim(), password);
      if (result.status === 'SOFTWARE_TOKEN_MFA_REQUIRED') {
        this.pendingMfaChallenge.set(result);
        this.loginErrorKey.set('auth.mfaRequired');
        this.loginErrorMessage.set('');
        return false;
      }
      await this.refreshProfile();
      this.pendingMfaChallenge.set(null);
      this.sessionExpired = false;
      this.sessionExpiredMessageKey.set('');
      this.loginErrorKey.set('');
      this.loginErrorMessage.set('');
      return true;
    } catch (error) {
      this.loginErrorKey.set('auth.invalidCredentials');
      this.loginErrorMessage.set(error instanceof Error ? error.message : 'Login failed.');
      return false;
    }
  }

  async completeSoftwareMfaLogin(code: string): Promise<boolean> {
    const challenge = this.pendingMfaChallenge();
    if (!challenge) {
      this.loginErrorKey.set('auth.mfaSessionExpired');
      this.loginErrorMessage.set('');
      return false;
    }

    try {
      await this.api.completeSoftwareMfaLogin(challenge.username, code, challenge.session);
      await this.refreshProfile();
      this.pendingMfaChallenge.set(null);
      this.sessionExpired = false;
      this.sessionExpiredMessageKey.set('');
      this.loginErrorKey.set('');
      this.loginErrorMessage.set('');
      return true;
    } catch (error) {
      this.loginErrorKey.set('auth.mfaInvalidCode');
      this.loginErrorMessage.set(error instanceof Error ? error.message : 'MFA verification failed.');
      return false;
    }
  }

  async loginWithGoogle(redirectTo = '/'): Promise<void> {
    await this.loginWithOAuthProvider('google', redirectTo);
  }

  async loginWithFacebook(redirectTo = '/'): Promise<void> {
    await this.loginWithOAuthProvider('facebook', redirectTo);
  }

  async loginWithAmazon(redirectTo = '/'): Promise<void> {
    await this.loginWithOAuthProvider('amazon', redirectTo);
  }

  async loginWithApple(redirectTo = '/'): Promise<void> {
    await this.loginWithOAuthProvider('apple', redirectTo);
  }

  async loginWithMicrosoft(redirectTo = '/'): Promise<void> {
    await this.loginWithOAuthProvider('microsoft', redirectTo);
  }

  async loginWithOrcid(redirectTo = '/'): Promise<void> {
    if (!this.isOrcidEnabled()) {
      this.showOAuthUnavailable('orcid');
      return;
    }
    await this.loginWithOAuthProvider('orcid', redirectTo);
  }

  async linkOrcid(redirectTo = '/security'): Promise<void> {
    if (!this.isOrcidEnabled()) {
      this.showOAuthUnavailable('orcid');
      return;
    }
    const currentUser = this.currentUser();
    if (!currentUser) {
      this.loginErrorMessage.set('A signed-in session is required to link ORCID.');
      return;
    }
    sessionStorage.setItem(this.oauthLinkModeStorageKey, 'orcid');
    sessionStorage.setItem(this.oauthRedirectStorageKey, redirectTo || '/security');
    await this.startOAuthAuthorize('orcid', redirectTo || '/security');
  }

  async unlinkOrcid(): Promise<void> {
    if (!this.isOrcidEnabled()) return;
    await this.updateProfile({ orcidId: '', orcidUri: '' });
  }

  async loginWithLinkedIn(redirectTo = '/'): Promise<void> {
    await this.loginWithOAuthProvider('linkedin', redirectTo);
  }

  async loginWithX(redirectTo = '/'): Promise<void> {
    await this.loginWithOAuthProvider('x', redirectTo);
  }


  isOrcidEnabled(): boolean {
    return this.api.runtimeConfig.orcidEnabled === true;
  }

  private showOAuthUnavailable(providerKey: keyof typeof this.oauthProviders): void {
    const provider = this.oauthProviders[providerKey];
    this.loginErrorKey.set(provider.unavailableKey);
    this.loginErrorMessage.set(`${provider.displayName} sign-in is not configured for this environment.`);
  }

  private async loginWithOAuthProvider(providerKey: keyof typeof this.oauthProviders, redirectTo = '/'): Promise<void> {
    const config = this.api.runtimeConfig;
    const provider = this.oauthProviders[providerKey];
    if (providerKey === 'x') {
      await this.startOAuthAuthorize(providerKey, redirectTo);
      return;
    }
    if (!config.cognitoHostedUiDomain || !config.cognitoUserPoolClientId) {
      this.loginErrorKey.set(provider.unavailableKey);
      this.loginErrorMessage.set(`${provider.displayName} sign-in is not configured for this environment.`);
      return;
    }

    sessionStorage.setItem(this.oauthStartAfterLogoutStorageKey, redirectTo || '/');
    sessionStorage.setItem(this.oauthResumeProviderStorageKey, providerKey);
    sessionStorage.setItem(this.oauthProviderAfterLogoutStorageKey, providerKey);
    this.pendingOAuthProvider.set(provider.displayName);
    this.clearLocalAuthenticatedSession();
    const logoutUrl = new URL(`${config.cognitoHostedUiDomain}/logout`);
    logoutUrl.searchParams.set('client_id', config.cognitoUserPoolClientId);
    logoutUrl.searchParams.set('logout_uri', this.oauthLogoutUrl());
    this.navigateToUrl(logoutUrl.toString());
  }

  private async startOAuthAuthorize(providerKey: keyof typeof this.oauthProviders, redirectTo = '/'): Promise<void> {
    const config = this.api.runtimeConfig;
    const provider = this.oauthProviders[providerKey];
    if (providerKey !== 'x' && (!config.cognitoHostedUiDomain || !config.cognitoUserPoolClientId)) {
      this.loginErrorKey.set(provider.unavailableKey);
      this.loginErrorMessage.set(`${provider.displayName} sign-in is not configured for this environment.`);
      return;
    }
    if (providerKey === 'x' && !config.xOAuthClientId) {
      this.loginErrorKey.set(provider.unavailableKey);
      this.loginErrorMessage.set(`${provider.displayName} sign-in is not configured for this environment.`);
      return;
    }

    const state = this.randomUrlSafeString(32);
    const codeVerifier = this.randomUrlSafeString(64);
    const codeChallenge = await this.sha256Base64Url(codeVerifier);
    const callbackUrl = this.oauthCallbackUrl();

    sessionStorage.setItem(this.oauthStateStorageKey, state);
    sessionStorage.setItem(this.oauthVerifierStorageKey, codeVerifier);
    sessionStorage.setItem(this.oauthRedirectStorageKey, redirectTo || '/');
    sessionStorage.setItem(this.oauthProviderAfterLogoutStorageKey, providerKey);
    this.pendingOAuthProvider.set(provider.displayName);

    const authorizeUrl = providerKey === 'x'
      ? new URL('https://x.com/i/oauth2/authorize')
      : new URL(`${config.cognitoHostedUiDomain}/oauth2/authorize`);
    if (providerKey !== 'x') authorizeUrl.searchParams.set('identity_provider', provider.name);
    authorizeUrl.searchParams.set('response_type', 'code');
    authorizeUrl.searchParams.set('client_id', providerKey === 'x' ? config.xOAuthClientId : config.cognitoUserPoolClientId);
    authorizeUrl.searchParams.set('redirect_uri', callbackUrl);
    authorizeUrl.searchParams.set('scope', provider.scopes);
    authorizeUrl.searchParams.set('state', state);
    authorizeUrl.searchParams.set('code_challenge_method', 'S256');
    authorizeUrl.searchParams.set('code_challenge', codeChallenge);

    this.navigateToUrl(authorizeUrl.toString());
  }

  async completeOAuthCallback(code: string, state: string): Promise<string> {
    const expectedState = sessionStorage.getItem(this.oauthStateStorageKey);
    const codeVerifier = sessionStorage.getItem(this.oauthVerifierStorageKey);
    const redirectTo = sessionStorage.getItem(this.oauthRedirectStorageKey) || '/';

    if (!expectedState || !codeVerifier) throw new Error('The OAuth login session has expired. Please try again.');
    if (state !== expectedState) throw new Error('OAuth state verification failed. Please try again.');

    const providerKey = this.oauthProviderKeyAfterLogout();
    const linkMode = sessionStorage.getItem(this.oauthLinkModeStorageKey);

    try {
      if (linkMode === 'orcid' && providerKey === 'orcid') {
        const originalTokens = this.api.authTokenSnapshot();
        await this.api.exchangeOAuthCodeForTokens(code, this.oauthCallbackUrl(), codeVerifier);
        const linkedOrcidId = this.api.currentOrcidIdFromIdToken();
        this.api.restoreAuthTokenSnapshot(originalTokens);
        if (!linkedOrcidId) {
          throw new Error('ORCID linking could not be completed. Please try again from the Security page.');
        }
        await this.updateProfile({ orcidId: linkedOrcidId, orcidUri: `https://orcid.org/${linkedOrcidId}` });
        await this.refreshProfile();
        const currentUser = this.currentUser();
        if (!currentUser?.orcidId?.trim()) {
          throw new Error('ORCID linking could not be completed. Please try again from the Security page.');
        }
        this.pendingMfaChallenge.set(null);
        this.sessionExpired = false;
        this.sessionExpiredMessageKey.set('');
        this.loginErrorKey.set('');
        this.loginErrorMessage.set('');
        return redirectTo || '/security';
      }

      if (providerKey === 'x') {
        sessionStorage.setItem(this.oauthCallbackCodeStorageKey, code);
        return '/auth/callback?x_email_required=1';
      }
      else await this.api.exchangeOAuthCodeForTokens(code, this.oauthCallbackUrl(), codeVerifier);
      await this.refreshProfile();
      const completedUser = this.currentUser();
      if (await this.maybeStartMicrosoftAvatarImport(completedUser, redirectTo)) return redirectTo;
      await this.requestOAuthEmailVerificationIfNeeded(completedUser);
      this.pendingMfaChallenge.set(null);
      this.sessionExpired = false;
      this.sessionExpiredMessageKey.set('');
      this.loginErrorKey.set('');
      this.loginErrorMessage.set('');
      if (this.hasMissingRequiredProfileFields(completedUser)) {
        const provider = this.requiredProfileCompletionProvider(completedUser);
        return `/account?complete=${provider}`;
      }
      return redirectTo;
    } catch (callbackError) {
      if (providerKey !== 'x' && linkMode !== 'orcid') this.clearLocalAuthenticatedSession();
      throw callbackError;
    } finally {
      if (this.oauthProviderKeyAfterLogout() !== 'x') this.clearOAuthSession();
    }
  }

  async completeXOAuthCallbackWithEmail(email: string): Promise<string> {
    const expectedState = sessionStorage.getItem(this.oauthStateStorageKey);
    const codeVerifier = sessionStorage.getItem(this.oauthVerifierStorageKey);
    const code = sessionStorage.getItem(this.oauthCallbackCodeStorageKey);
    const redirectTo = sessionStorage.getItem(this.oauthRedirectStorageKey) || '/';
    if (!expectedState || !codeVerifier || !code) throw new Error('The X login session has expired. Please try again.');

    try {
      await this.api.exchangeXOAuthCodeForTokens(code, this.oauthCallbackUrl(), codeVerifier, { email: email.trim().toLowerCase() });
      await this.refreshProfile();
      const completedUser = this.currentUser();
      await this.requestOAuthEmailVerificationIfNeeded(completedUser);
      this.pendingMfaChallenge.set(null);
      this.sessionExpired = false;
      this.sessionExpiredMessageKey.set('');
      this.loginErrorKey.set('');
      this.loginErrorMessage.set('');
      if (this.hasMissingRequiredProfileFields(completedUser)) return `/account?complete=${this.requiredProfileCompletionProvider(completedUser)}`;
      return redirectTo;
    } finally {
      this.clearOAuthSession();
    }
  }

  logout(redirectToLogin = false): void {
    this.api.logout();
    this.currentUser.set(null);
    this.adminStatus.set(null);
    this.pendingMfaChallenge.set(null);
    this.restorePromise = null;
    void this.router.navigate([redirectToLogin ? '/login' : '/']);
  }

  expireSession(): void {
    if (this.sessionExpired) return;
    this.sessionExpired = true;
    this.sessionExpiredMessageKey.set('auth.sessionExpired');
    this.logout(true);
  }

  showLoginError(message: string, messageKey = 'auth.oauthSignInFailed'): void {
    this.loginErrorKey.set(messageKey);
    this.loginErrorMessage.set(message.trim());
  }

  async updateProfile(profile: Partial<UserProfile>): Promise<void> {
    const previousUser = this.currentUser();
    const updatedUser = await this.api.updateProfile(profile);
    this.setCurrentUser(this.withSubmittedOrcidCompletionFields(previousUser, updatedUser, profile));
  }

  oauthCallbackProviderLabel(): string {
    const providerKey = sessionStorage.getItem(this.oauthProviderAfterLogoutStorageKey);
    if (providerKey && providerKey in this.oauthProviders) {
      return this.oauthProviders[providerKey as keyof typeof this.oauthProviders].displayName;
    }
    return this.pendingOAuthProvider() || 'OAuth';
  }

  async exportPrivacyData(): Promise<PrivacyExportRequestResponse> {
    return this.api.exportPrivacyData();
  }

  async deleteAccountForPrivacy(): Promise<void> {
    await this.api.deletePrivacyAccount();
    this.logout(true);
  }

  async refreshProfile(): Promise<void> {
    this.setCurrentUser(await this.api.getProfile());
    void this.ensureAdmin();
  }

  async maybeStartMicrosoftAvatarImport(user: UserProfile | null, redirectTo = window.location.pathname + window.location.search): Promise<boolean> {
    if (this.isMicrosoftAvatarImportCallbackReturn()) return false;
    if (!user || user.loginProvider !== 'microsoft') return false;
    if (user.avatar || user.avatarStatus === 'READY') return false;
    if (user.avatarImportEnabled === false) return false;
    if (user.oauthAvatarCheckedAt && !this.isOlderThan(user.oauthAvatarCheckedAt, 24 * 60 * 60 * 1000)) return false;
    const attemptKey = `${this.microsoftAvatarImportAttemptStorageKey}.${user.username}`;
    if (sessionStorage.getItem(attemptKey) === '1') return false;
    sessionStorage.setItem(attemptKey, '1');
    try {
      const result = await this.api.startMicrosoftAvatarImport(redirectTo || '/');
      if (result.status === 'REDIRECT' && result.authorizeUrl) {
        this.navigateToUrl(result.authorizeUrl);
        return true;
      }
    } catch {
      // Avatar import is best-effort: login/profile loading must keep using initials fallback.
    }
    return false;
  }

  async ensureSession(): Promise<boolean> {
    if (this.currentUser()) return true;
    if (!this.api.hasToken) return false;

    this.restorePromise ??= this.restoreSession();
    return this.restorePromise;
  }

  async ensureAdmin(): Promise<boolean> {
    if (!await this.ensureSession()) return false;
    if (this.adminStatus()?.isAdmin) return true;
    try {
      const status = await this.api.getAdminMe();
      this.adminStatus.set(status);
      return status.isAdmin;
    } catch {
      this.adminStatus.set({ isAdmin: false, isModerator: false });
      return false;
    }
  }

  async ensureModerator(): Promise<boolean> {
    if (!await this.ensureSession()) return false;
    if (this.adminStatus()?.isModerator || this.adminStatus()?.isAdmin) return true;
    try {
      const status = await this.api.getAdminMe();
      this.adminStatus.set(status);
      return status.isModerator || status.isAdmin;
    } catch {
      this.adminStatus.set({ isAdmin: false, isModerator: false });
      return false;
    }
  }

  async updateProfilePicture(file: File): Promise<void> {
    const upload = await this.api.requestProfilePictureUpload(file);
    await this.api.uploadProfilePicture(upload, file);
    await this.pollProfilePictureChange(this.currentUser()?.avatar ?? '');
  }

  async deleteProfilePicture(): Promise<void> {
    await this.api.deleteProfilePicture();
    this.setCurrentUser(await this.api.getProfile(), { keepPendingPictureDeletion: true });
    void this.ensureAdmin();
  }

  private setCurrentUser(user: UserProfile, options: { keepPendingPictureDeletion?: boolean } = {}): void {
    this.currentUser.set(user);
    if (!options.keepPendingPictureDeletion) this.clearPendingPictureDeletionIfProfileHasAvatar(user.avatar ?? '');
  }

  private hasMissingRequiredProfileFields(user: UserProfile | null): boolean {
    if (!user || (!user.orcidId && user.loginProvider !== 'x' && user.avatarImportProvider !== 'x')) return false;
    return !user.firstName?.trim() || !user.lastName?.trim() || !user.email?.trim();
  }

  private requiredProfileCompletionProvider(user: UserProfile | null): string {
    return user?.loginProvider === 'x' || user?.avatarImportProvider === 'x' ? 'x' : 'orcid';
  }

  private withSubmittedOrcidCompletionFields(previousUser: UserProfile | null, updatedUser: UserProfile, submittedProfile: Partial<UserProfile>): UserProfile {
    if (!previousUser?.orcidId && !updatedUser.orcidId) return updatedUser;
    const explicitlySubmittedOrcidId = Object.prototype.hasOwnProperty.call(submittedProfile, 'orcidId');
    const explicitlySubmittedOrcidUri = Object.prototype.hasOwnProperty.call(submittedProfile, 'orcidUri');
    return {
      ...updatedUser,
      orcidId: updatedUser.orcidId || (explicitlySubmittedOrcidId ? submittedProfile.orcidId : previousUser?.orcidId),
      orcidUri: updatedUser.orcidUri || (explicitlySubmittedOrcidUri ? submittedProfile.orcidUri : previousUser?.orcidUri),
      orcidConnectedAt: updatedUser.orcidConnectedAt || (explicitlySubmittedOrcidId && !submittedProfile.orcidId ? undefined : previousUser?.orcidConnectedAt),
      firstName: updatedUser.firstName?.trim() ? updatedUser.firstName : submittedProfile.firstName ?? updatedUser.firstName,
      lastName: updatedUser.lastName?.trim() ? updatedUser.lastName : submittedProfile.lastName ?? updatedUser.lastName,
      email: updatedUser.email?.trim() ? updatedUser.email : submittedProfile.email ?? updatedUser.email
    };
  }

  private async requestOAuthEmailVerificationIfNeeded(user: UserProfile | null): Promise<void> {
    if (!user?.email?.trim() || user.emailVerified !== false) return;
    try {
      await this.api.requestEmailVerification();
    } catch (error) {
      console.warn('Unable to request OAuth email verification.', error);
    }
  }

  private clearPendingPictureDeletionIfProfileHasAvatar(avatar: string): void {
    if (!avatar) return;
    try {
      const pendingDeletedAvatar = localStorage.getItem(this.pendingPictureDeletionStorageKey);
      if (pendingDeletedAvatar && pendingDeletedAvatar !== avatar) {
        localStorage.removeItem(this.pendingPictureDeletionStorageKey);
      }
    } catch { /* Ignore unavailable storage. */ }
  }

  private async restoreSession(): Promise<boolean> {
    this.isRestoringSession.set(true);
    try {
      try {
        await this.refreshProfile();
      } catch {
        await this.api.refreshSession();
        await this.refreshProfile();
      }
      this.sessionExpired = false;
      void this.maybeStartMicrosoftAvatarImport(this.currentUser());
      return true;
    } catch (error) {
      this.logout(true);
      return false;
    } finally {
      this.isRestoringSession.set(false);
      this.restorePromise = null;
    }
  }

  private oauthCallbackUrl(): string {
    return `${this.oauthRedirectOrigin()}/auth/callback`;
  }

  private oauthLogoutUrl(): string {
    return `${this.oauthRedirectOrigin()}/login`;
  }

  private oauthRedirectOrigin(): string {
    return window.location.origin;
  }

  private clearMicrosoftAvatarImportAttemptsAfterCallback(): void {
    if (!this.isMicrosoftAvatarImportCallbackReturn()) return;
    try {
      const prefix = `${this.microsoftAvatarImportAttemptStorageKey}.`;
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
      }
    } catch { /* Ignore unavailable storage. */ }
  }

  private isMicrosoftAvatarImportCallbackReturn(): boolean {
    const avatarImportResult = new URLSearchParams(window.location.search).get('avatar_import');
    return avatarImportResult === 'success' || avatarImportResult === 'failed';
  }

  private navigateToUrl(url: string): void {
    window.location.assign(url);
  }

  private clearLocalAuthenticatedSession(): void {
    this.api.logout();
    this.currentUser.set(null);
    this.adminStatus.set(null);
    this.pendingMfaChallenge.set(null);
    this.restorePromise = null;
    this.sessionExpired = false;
    this.sessionExpiredMessageKey.set('');
  }

  private isOAuthCallbackRequest(): boolean {
    return window.location.pathname === '/auth/callback';
  }

  private isHostedUiLogoutReturn(): boolean {
    return this.oauthResumeProviderKeyAfterLogout() !== null;
  }

  private resumeOAuthAfterHostedUiLogout(): boolean {
    if (!this.isHostedUiLogoutReturn()) return false;
    const providerKey = this.oauthResumeProviderKeyAfterLogout();
    if (!providerKey) return false;
    const redirectTo = sessionStorage.getItem(this.oauthStartAfterLogoutStorageKey) || '/';
    sessionStorage.removeItem(this.oauthStartAfterLogoutStorageKey);
    sessionStorage.removeItem(this.oauthResumeProviderStorageKey);
    void this.startOAuthAuthorize(providerKey, redirectTo);
    return true;
  }

  private oauthResumeProviderKeyAfterLogout(): keyof typeof this.oauthProviders | null {
    const providerKey = sessionStorage.getItem(this.oauthResumeProviderStorageKey);
    return providerKey && providerKey in this.oauthProviders ? providerKey as keyof typeof this.oauthProviders : null;
  }

  private oauthProviderKeyAfterLogout(): keyof typeof this.oauthProviders | null {
    const providerKey = sessionStorage.getItem(this.oauthProviderAfterLogoutStorageKey);
    return providerKey && providerKey in this.oauthProviders ? providerKey as keyof typeof this.oauthProviders : null;
  }

  private clearOAuthSession(): void {
    sessionStorage.removeItem(this.oauthStartAfterLogoutStorageKey);
    sessionStorage.removeItem(this.oauthResumeProviderStorageKey);
    sessionStorage.removeItem(this.oauthProviderAfterLogoutStorageKey);
    sessionStorage.removeItem(this.oauthStateStorageKey);
    sessionStorage.removeItem(this.oauthVerifierStorageKey);
    sessionStorage.removeItem(this.oauthRedirectStorageKey);
    sessionStorage.removeItem(this.oauthCallbackCodeStorageKey);
    sessionStorage.removeItem(this.oauthLinkModeStorageKey);
    sessionStorage.removeItem(this.oauthLinkModeStorageKey);
    this.pendingOAuthProvider.set('');
  }

  private randomUrlSafeString(byteLength: number): string {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return this.base64Url(bytes);
  }

  private async sha256Base64Url(value: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return this.base64Url(new Uint8Array(digest));
  }

  private base64Url(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach(byte => { binary += String.fromCharCode(byte); });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  private isOlderThan(isoDate: string, maxAgeMs: number): boolean {
    const parsed = Date.parse(isoDate);
    return Number.isNaN(parsed) || Date.now() - parsed > maxAgeMs;
  }

  private async pollProfilePictureChange(previousAvatar: string): Promise<void> {
    const maxAttempts = 15;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await new Promise(resolve => setTimeout(resolve, attempt === 0 ? 1000 : 2000));
      await this.refreshProfile();
      const user = this.currentUser();
      if (user?.avatar && user.avatar !== previousAvatar && user.avatarStatus !== 'PROCESSING') return;
    }
  }
}
